import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import {
  View,
  TextInput,
  TextInputProps,
  Platform,
} from 'react-native';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';

import { AvatarImage } from '../common/AvatarImage';
import { Line } from '../common/Line';
import { Separator } from '../common/Separator';
import { TextTouchable } from '../common/TextTouchable';
import { useColors } from '@hooks';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { IS_WEB, passIf } from '@utils';

const Container = styled(View)({
  flexDirection: 'row',
});

const RowContainer = styled(View)({
  flexDirection: 'row',
});

const SubmitButtonContainer = styled(View)({
  flexShrink: 1,
  justifyContent: 'flex-end',
});

const EditorContainer = styled(View)({
  flex: 1,
});

const InputContainer = styled(View)({
  flex: 1,
  justifyContent: 'center',
});

const Input = styled(TextInput)({
  color: '$text400',
  variant: 'text.medium',
  ...Platform.select({
    web: { outlineStyle: 'none' },
    default: {},
  }),
});

interface IAddCommentTextStyleProps {
  disabled?: boolean;
}

const SubmitText = styled(TextTouchable)(
  (props: IAddCommentTextStyleProps) => ({
    color: props.disabled ? '$text100' : '$heading800',
    variant: 'text.medium',
  }),
);

export type ViewerTextInputRefType = TextInput | null;

export interface IViewerTextInputProps extends TextInputProps {
  maxHeight?: number;
  onSubmit?(): any;
  submitText?: string;
  isValid?: boolean;
}

export const ViewerTextInput = observer(
  forwardRef<ViewerTextInputRefType, IViewerTextInputProps>(
    ({ onSubmit, submitText, isValid, maxHeight, ...props }, ref) => {
      const viewer = useViewerModel();
      const colors = useColors();
      const inputRef = useRef<TextInput>(null);

      useImperativeHandle(ref, () => inputRef.current!);

      useEffect(() => {
        resizeInput();
      }, [props.value]);

      function resizeInput() {
        const element = inputRef?.current as unknown as HTMLElement;

        if (IS_WEB && element?.scrollHeight) {
          // We set height = "auto" for component shrinking
          element.style.height = 'auto';
          const height = maxHeight
            ? Math.min(element.scrollHeight, maxHeight)
            : element.scrollHeight;
          element.style.height = `${height}px`;
        }
      }

      return (
        <Container>
          <AvatarImage
            size={36}
            source={{ uri: viewer.currentUser?.photo.small?.url }}
          />

          <Separator size="$5" vertical />

          <EditorContainer>
            <RowContainer>
              <InputContainer>
                <Input
                  {...props}
                  ref={inputRef}
                  multiline
                  // On the web this value defaults to 2, showing 2 lines of text
                  // This change ensures it matches the design by displaying 1 line of text instead
                  numberOfLines={passIf(IS_WEB, 1)}
                  placeholderTextColor={colors.$text80}
                />
              </InputContainer>

              <Separator size="$5" vertical />

              {!!onSubmit && (
                <SubmitButtonContainer>
                  <SubmitText disabled={!isValid} onPress={onSubmit}>
                    {submitText}
                  </SubmitText>
                </SubmitButtonContainer>
              )}
            </RowContainer>

            <Separator size="$3" />
            <Line color="$background50" />
          </EditorContainer>
        </Container>
      );
    },
  ),
);
